import { useCallback, useState } from "react";
import type { UseEditQuizStateProps } from "./useEditQuizState";

type UseThemeStateProps = Pick<
	UseEditQuizStateProps,
	"proctoringAssessmentConfiguration" | "quizInfo" | "quizType" | "checkThemeExists"
>;

/**Anything related to theme */
export default function useThemeState({
	proctoringAssessmentConfiguration,
	quizInfo,
	quizType,
	checkThemeExists,
}: UseThemeStateProps) {
	/* Canvas Theme Disclaimer */
	const [themeExists, setThemeExists] = useState<boolean>(false);
	const [verifyingTheme, setVerifyingTheme] = useState<boolean>(false);
	/* Page can be called asynchronously, use state to determine if background steps have been completed */
	const [backgroundStepsComplete, setBackgroundStepsComplete] = useState<boolean>(false);

	const runCanvasThemeValidator = useCallback(() => {
		if (!checkThemeExists) {
			setThemeExists(false);
			return;
		}

		checkThemeExists()
			.then((res) => {
				setThemeExists(res);
			})
			.catch(async (error) => {
				console.error(`Proxy message "checkThemeExists" failed, attempting fallback:`, error);
				/**
				 * @deprecated remove after extension rollout
				 */
				const doesThemeExist: boolean = await chrome.runtime.sendMessage(import.meta.env.VITE_EXTENSION_ID as string, {
					request: "checkThemeExists",
				});
				// eslint-disable-next-line deprecation/deprecation
				setThemeExists(doesThemeExist);
			})
			.catch((error) => {
				console.error(`"checkThemeExists" fallback failed:`, error);
				setThemeExists(false);
			});

		return;
	}, [checkThemeExists]);

	const handleFetchAndSetTheme = useCallback(() => {
		if (backgroundStepsComplete) {
			return;
		}

		/* Handle according to LMS */
		if (quizInfo.lms === "canvas") {
			/* Page has not received configuration */
			if (!proctoringAssessmentConfiguration) {
				return;
			}

			/* Check if quiz is third party */
			if (!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption && quizType === "lms") {
				/* If not, verify if theme exists */
				setVerifyingTheme(true);
				runCanvasThemeValidator();
				setVerifyingTheme(false);
			}

			setBackgroundStepsComplete(true);
		} else {
			/* Implement other LMS background steps if required via additional conditions */
			setBackgroundStepsComplete(true);
		}
	}, [backgroundStepsComplete, proctoringAssessmentConfiguration, quizInfo.lms, quizType, runCanvasThemeValidator]);

	return {
		state: {
			themeExists,
			verifyingTheme,
		},
		action: {
			handleFetchAndSetTheme,
		},
	};
}
